export type PaymentTrackingStatus = "Paid" | "Pending" | "Overdue";

export const PAYMENT_TRACKING_STATUS_OPTIONS: PaymentTrackingStatus[] = [
  "Pending",
  "Overdue",
  "Paid",
];

export interface PaymentStatusHistoryItem {
  id?: string | number;
  received_amount?: string | number;
  received_date?: string;
  pending_amount?: string | number;
  remarks?: string;
}

export interface PaymentStatusApiRecord {
  id?: string;
  invoice_number?: string;
  client_name?: string | null;
  project?: string | null;
  frequency?: string | null;
  invoice_date?: string;
  billing_period_from?: string;
  billing_period_to?: string;
  subtotal?: string | number;
  tax_total?: string | number;
  grand_total?: string | number;
  status?: string;
  received_amount?: string | number;
  pending_amount?: string | number;
  payment_history?: PaymentStatusHistoryItem[];
  created_on?: string;
  updated_on?: string;
}

export interface PaymentStatusPaginatedApiResponse {
  success?: { code?: string; message?: string };
  links?: { next?: string | null; previous?: string | null };
  count?: number;
  page?: number;
  page_size?: number;
  results?: PaymentStatusApiRecord[];
}

export interface UpdatePaymentStatusHistoryPayload {
  received_amount: number;
  received_date: string;
}

export interface UpdatePaymentStatusPayload {
  status: string;
  payment_history?: UpdatePaymentStatusHistoryPayload[];
}

export interface PaymentHistoryEntry {
  id: string;
  receivedAmount: number;
  receivedDate: string;
  pendingAmount: number;
  remarks: string;
}

export interface PaymentStatusRecord {
  recordId: string;
  id: string;
  client: string;
  project?: string;
  frequency?: string;
  period: string;
  amount: number;
  gst: number;
  total: number;
  status: PaymentTrackingStatus;
  invoiceDate: string | null;
  received?: number;
  receivedDate?: string | null;
  pending?: number;
  nextDate?: string | null;
  receivedItems?: ReceivedPaymentItem[];
  paymentHistory: PaymentHistoryEntry[];
}

export type InvoiceStatus =
  | "Paid"
  | "Sent"
  | "Overdue"
  | "Pending"
  | "Draft"
  | "On Hold";

export const INVOICE_STATUS_OPTIONS: InvoiceStatus[] = [
  "Draft",
  "Pending",
  "Sent",
  "On Hold",
  "Paid",
  "Overdue",
];

export interface ReceivedPaymentItem {
  id: string;
  amount: number;
  date: string;
}

export interface Invoice {
  id: string;
  client: string;
  project?: string;
  period: string;
  amount: number;
  gst: number;
  total: number;
  status: InvoiceStatus;
  sent: string | null;
  paid: string | null;
  received?: number;
  receivedDate?: string | null;
  pending?: number;
  paidDate?: string | null;
  receivedItems?: ReceivedPaymentItem[];
}

export type BillingConfigStatus = "Active" | "Draft" | "On Hold";

export interface BillingConfig {
  rawId?: string;
  project: string;
  client: string;
  type: string;
  frequency: string;
  amount: number;
  cgstRate: string;
  sgstRate: string;
  igstRate: string;
  status: BillingConfigStatus;
}

export interface BankDetails {
  bankName: string;
  accountName: string;
  accountNumber: string;
  branchName: string;
  ifscCode: string;
}

export interface PaymentTermsDetails {
  dueDays: number;
  bank: BankDetails;
}

export interface InvoicePreviewLineItem {
  id: string;
  title: string;
  subtitle: string;
  hsnSac: string;
  qty: number;
  rate: number;
  amount: number;
  billingPeriod: string;
  gstApplicable: boolean;
  gstRate: number;
  billingType: string;
  frequency: string;
  billingStartDate: string;
  currency: string;
}

export interface LineItemTaxBreakdown {
  cgst: number;
  sgst: number;
  igst: number;
  taxTotal: number;
  halfGstRate: number;
  fullGstRate: number;
  isInstate: boolean;
}

export interface ProjectClientData {
  id: string;
  is_active: boolean;
  client_project: string;
  client_name: string;
  address: string;
  mobile_number: string;
  email: string;
  gst_types: string;
  gst_no?: string;
  gst_applicable?: boolean;
  gst_rate?: string | number;
}

export interface InvoiceAddressBlock {
  name: string;
  lines: string[];
  gstin: string;
}

export interface SaveBillingItemPayload {
  amount: number;
  currency: string;
  hsn_sac: string;
  qty: number;
  cgst: number;
  sgst: number;
  igst: number;
  billing_type: string;
  frequency: string;
  binning_start_date: string;
}

export interface SaveBillingPayload {
  invoice_number: string;
  client_billing_email: string;
  cc_emails: string[];
  accountant_review_email: string;
  reminder: number;
  grace_period: number;
  next_reminder_date: string;
  notes: string;
  project: string;
  client_name: string;
  items: SaveBillingItemPayload[];
}

export interface SaveInvoiceItemPayload {
  description: string;
  hsn_sac: string;
  qty: number;
  rate: number;
  amount: number;
  cgst: number;
  sgst: number;
  igst: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  total: number;
  billing_period_from: string;
  billing_period_to: string | null;
}

export interface SaveInvoicePayload {
  invoice_title: string;
  invoice_number: string;
  invoice_date: string;
  billing_period_from: string;
  billing_period_to: string | null;
  from_details: string;
  to_details: string;
  project: string;
  client_name: string;
  client_billing_email: string;
  cc_emails: string[];
  notes: string;
  billing_id: string;
  items: SaveInvoiceItemPayload[];
  subtotal: number;
  tax_total: number;
  grand_total: number;
}
